import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type { SessionAuditLog, SessionRecord } from "../types";
import { useToast } from "../toast";

type SessionDetailRecord = SessionRecord & { audit_logs?: SessionAuditLog[] };

export default function SessionDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const api = useAuthApi();
  const toast = useToast();
  const [session, setSession] = useState<SessionDetailRecord | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await api<SessionDetailRecord>(`${MGMT}/sessions/${id}`);
      setSession(detail);
    } catch (e) {
      toast.show((e as Error).message, "error");
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [api, id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const timelineItems = useMemo(() => {
    return (session?.audit_logs ?? []).slice().sort((a, b) => {
      if (a.created_at === b.created_at) return a.id - b.id;
      return a.created_at < b.created_at ? -1 : 1;
    });
  }, [session?.audit_logs]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (!session) return <Typography color="error">Session not found</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>Session #{session.id}</Typography>
        <Chip label={session.status} color={statusColor(session.status)} />
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
            <Typography variant="body2"><strong>Charger:</strong> {session.charge_point_id}</Typography>
            <Typography variant="body2"><strong>Connector:</strong> {session.connector_id ?? "—"}</Typography>
            <Typography variant="body2"><strong>Start:</strong> {fmtDate(session.start_time)}</Typography>
            <Typography variant="body2"><strong>Stop:</strong> {fmtDate(session.stop_time)}</Typography>
            <Typography variant="body2">
              <strong>Energy:</strong> {session.energy_kwh != null ? `${session.energy_kwh.toFixed(2)} kWh` : "—"}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1 }}>Timeline</Typography>
            <Button size="small" onClick={load}>Refresh</Button>
          </Stack>
          {timelineItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No timeline events yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {timelineItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.25,
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
                      {fmtDate(item.created_at)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {item.event_type}
                    {item.raw_ocpp_error_code ? ` • ${item.raw_ocpp_error_code}` : ""}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </>
  );
}
